import React, { useState } from "react";

const DigitalMarketing = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const syllabusData = {
    courseOverview: {
      title: "Course Overview",
      content: (
        <>
          <p className="font-bold text-sm">Objective:</p> Master the essentials
          of Digital Marketing and develop skills to create effective online
          marketing campaigns.
          <p className="font-bold text-sm">Duration:</p> 30 days, 1 hour per
          day.
          <p className="font-bold text-sm">Prerequisites:</p> Basic computer
          skills, interest in marketing, and familiarity with social media
          platforms.
          <p className="font-bold text-sm">Tools Needed:</p> Google Analytics,
          Google Ads, Facebook Ads Manager, Canva, Hootsuite, and a web browser.
        </>
      ),
    },
    dailySchedule: {
      title: "Daily Schedule",
      days: [
        {
          day: "Day 1: Introduction to Digital Marketing",
          objective:
            "Understand the basics of digital marketing and its importance.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is Digital Marketing? Evolution and scope.</li>
                <li>Key differences from traditional marketing.</li>
                <li>Overview of digital marketing channels.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Explore popular digital marketing platforms (e.g., Google
                  Ads).
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 2: Understanding the Digital Marketing Landscape",
          objective: "Learn about the digital ecosystem and key players.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of SEO, SEM, SMM, and email marketing.</li>
                <li>Role of analytics in digital marketing.</li>
                <li>Introduction to marketing funnels.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a basic marketing funnel diagram.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 3: Website Basics for Marketing",
          objective: "Understand the role of websites in digital marketing.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Importance of a business website.</li>
                <li>Key elements of a marketing-friendly website.</li>
                <li>Introduction to WordPress or Wix.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Sign up for a free website builder and explore features.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 4: Search Engine Optimization (SEO) Basics",
          objective: "Learn the fundamentals of SEO.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is SEO and how it works.</li>
                <li>Keywords research and analysis.</li>
                <li>On-page SEO basics (titles, meta tags).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Use a keyword tool (e.g., Google Keyword Planner) to find
                  keywords.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 5: Advanced SEO Techniques",
          objective: "Master advanced SEO strategies.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Off-page SEO (backlinks, guest posting).</li>
                <li>Technical SEO (site speed, mobile optimization).</li>
                <li>Using SEO tools (e.g., SEMrush, Ahrefs).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Optimize a sample webpage for SEO.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 6: Introduction to Search Engine Marketing (SEM)",
          objective: "Understand paid search marketing.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is SEM and how it differs from SEO.</li>
                <li>Overview of Google Ads platform.</li>
                <li>Key terms (PPC, CPC, CTR).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a Google Ads account.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 7: Creating and Managing Google Ads Campaigns",
          objective: "Learn to set up and manage paid campaigns.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Setting up a Google Ads campaign.</li>
                <li>Ad groups, keywords, and bids.</li>
                <li>Monitoring and optimizing campaigns.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Launch a sample Google Ads campaign.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 8: Social Media Marketing (SMM) Basics",
          objective: "Explore social media marketing fundamentals.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Role of social media in marketing.</li>
                <li>Popular platforms (Facebook, Instagram, LinkedIn).</li>
                <li>Creating a social media strategy.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Outline a social media strategy for a brand.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 9: Content Creation for Social Media",
          objective: "Learn to create engaging social media content.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Types of content (posts, stories, videos).</li>
                <li>Using Canva for graphics design.</li>
                <li>Content scheduling tools (e.g., Hootsuite).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Design a social media post using Canva.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 10: Running Facebook and Instagram Ads",
          objective: "Master paid social media advertising.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Facebook Ads Manager.</li>
                <li>Creating targeted ad campaigns.</li>
                <li>Analyzing ad performance.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Set up a sample Facebook ad campaign.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 11: Email Marketing Basics",
          objective: "Understand the role of email in digital marketing.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Benefits of email marketing.</li>
                <li>Building an email list.</li>
                <li>Introduction to tools (e.g., Mailchimp).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Sign up for Mailchimp and explore its features.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 12: Creating Effective Email Campaigns",
          objective: "Learn to design and send email campaigns.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Crafting compelling subject lines.</li>
                <li>Designing email templates.</li>
                <li>Tracking email performance metrics.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create and send a sample email campaign.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 13: Introduction to Google Analytics",
          objective: "Learn to track and analyze website traffic.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is Google Analytics?</li>
                <li>Setting up Google Analytics on a website.</li>
                <li>Understanding key metrics (sessions, bounce rate).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Install Google Analytics on a sample site.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 14: Analyzing Data with Google Analytics",
          objective: "Master data analysis for marketing insights.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Interpreting audience and behavior reports.</li>
                <li>Conversion tracking and goal setting.</li>
                <li>Using custom reports and dashboards.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a custom dashboard in Google Analytics.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 15: Content Marketing Fundamentals",
          objective: "Understand the role of content in digital marketing.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is content marketing?</li>
                <li>Creating blogs, articles, and videos.</li>
                <li>Content distribution strategies.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write a sample blog post outline.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 16: Video Marketing Strategies",
          objective: "Learn to leverage video for marketing.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Importance of video marketing.</li>
                <li>
                  Creating videos with tools (e.g., Canva, Adobe Express).
                </li>
                <li>Optimizing videos for YouTube and social media.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a short promotional video.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 17: Affiliate Marketing Basics",
          objective: "Explore affiliate marketing opportunities.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is affiliate marketing?</li>
                <li>Joining affiliate programs (e.g., Amazon Associates).</li>
                <li>Earning through referrals and commissions.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Sign up for an affiliate program and get a referral link.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 18: Influencer Marketing",
          objective: "Learn to collaborate with influencers.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is influencer marketing?</li>
                <li>Finding and vetting influencers.</li>
                <li>Creating influencer campaigns.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Identify potential influencers for a campaign.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 19: Mobile Marketing Strategies",
          objective: "Understand mobile marketing techniques.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Importance of mobile marketing.</li>
                <li>SMS marketing and push notifications.</li>
                <li>Optimizing for mobile devices.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Draft a mobile marketing message.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 20: Conversion Rate Optimization (CRO)",
          objective: "Learn to improve website conversions.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is CRO and its importance.</li>
                <li>A/B testing and heatmaps.</li>
                <li>Optimizing landing pages.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Design an A/B test for a landing page.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 21: E-commerce Marketing",
          objective: "Explore marketing strategies for online stores.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>E-commerce marketing overview.</li>
                <li>Product listings and promotions.</li>
                <li>Using marketplaces (e.g., Amazon, eBay).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a product listing for an online store.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 22: Marketing Analytics and Reporting",
          objective: "Master analytics for campaign performance.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Key performance indicators (KPIs).</li>
                <li>Generating reports in Google Analytics and Ads.</li>
                <li>Interpreting data for decision-making.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a marketing report using sample data.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 23: Personal Branding in Digital Marketing",
          objective: "Learn to build a personal brand online.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Importance of personal branding.</li>
                <li>Creating a LinkedIn profile and portfolio.</li>
                <li>Engaging with online communities.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Update a LinkedIn profile with marketing skills.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 24: Legal and Ethical Considerations",
          objective:
            "Understand legal and ethical aspects of digital marketing.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>GDPR and data privacy laws.</li>
                <li>Ethical advertising practices.</li>
                <li>Avoiding deceptive marketing techniques.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Review a campaign for compliance with GDPR.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 25: Integrating Marketing Channels",
          objective: "Learn to create an integrated marketing strategy.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Combining SEO, SEM, SMM, and email.</li>
                <li>Creating a cohesive brand message.</li>
                <li>Measuring cross-channel performance.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Design an integrated marketing plan.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 26: Final Project – Planning",
          objective: "Plan the final digital marketing project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of the final project.</li>
                <li>
                  Suggested project ideas (e.g., campaign launch, social media
                  strategy).
                </li>
                <li>Planning campaign goals and metrics.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a project plan and timeline.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 27: Final Project – Campaign Setup",
          objective: "Set up the digital marketing campaign.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Selecting channels and tools.</li>
                <li>Creating content and ads.</li>
                <li>Setting up tracking (e.g., Google Analytics).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Set up the campaign infrastructure.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 28: Final Project – Campaign Execution",
          objective: "Launch and monitor the marketing campaign.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Launching the campaign across channels.</li>
                <li>Real-time monitoring and adjustments.</li>
                <li>Ensuring compliance and ethics.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Launch the campaign and monitor initial results.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 29: Final Project – Analysis and Optimization",
          objective: "Analyze campaign performance and optimize.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Analyzing metrics (CTR, conversion rate).</li>
                <li>Optimizing underperforming elements.</li>
                <li>Creating a performance report.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Optimize the campaign and generate a report.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 30: Final Project – Presentation",
          objective: "Present the completed digital marketing project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Preparing a presentation or demo.</li>
                <li>Discussing challenges and solutions.</li>
                <li>Presenting to peers or instructors.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Present the final campaign results.</li>
              </ul>
            </>
          ),
        },
      ],
    },
    learningOutcomes: {
      title: "Learning Outcomes",
      content: (
        <>
          <p className="text-sm">
            By the end of this course, learners will be able to:
          </p>
          <ul className="list-disc pl-4 text-xs">
            <li>
              Develop and execute digital marketing campaigns across multiple
              channels.
            </li>
            <li>
              Analyze website traffic and campaign performance using analytics
              tools.
            </li>
            <li>
              Create engaging content for social media and email marketing.
            </li>
            <li>
              Complete a comprehensive digital marketing project with measurable
              results.
            </li>
          </ul>
        </>
      ),
    },
    finalProjectIdeas: {
      title: "Suggested Final Project Ideas",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              <span className="font-bold text-sm">Social Media Campaign:</span>{" "}
              Launch a multi-platform social media marketing campaign.
            </li>
            <li>
              <span className="font-bold text-sm">Google Ads Campaign:</span>{" "}
              Create and optimize a pay-per-click campaign.
            </li>
            <li>
              <span className="font-bold text-sm">
                Email Marketing Strategy:
              </span>{" "}
              Design and execute an email marketing campaign for a brand.
            </li>
          </ul>
        </>
      ),
    },
    additionalNotes: {
      title: "Additional Notes",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              Practice with real-world case studies (e.g., Google Analytics
              demos).
            </li>
            <li>
              Explore additional resources (e.g., HubSpot Academy, Google
              Digital Garage).
            </li>
            <li>Use discussion time for Q&A and campaign feedback.</li>
          </ul>
          <p className="text-sm">
            This syllabus provides a structured progression from digital
            marketing basics to advanced strategies over 30 days.
          </p>
        </>
      ),
    },
  };

  // Function to handle enrollment (replace with actual enrollment logic)
  const handleEnroll = () => {
    alert("Enroll Now clicked! (Add your enrollment logic here)");
  };

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      {/* Hero Section */}
      <header className="bg-gradient-to-r from-slate-600 to-indigo-600 py-8 text-center">
        <h1 className="text-2xl font-bold mb-1 text-white">
          Digital Marketing Syllabus
        </h1>
        <p className="text-sm text-white">
          Master Digital Marketing (30 Hours – 1 Hour Per Day)
        </p>
      </header>

      {/* Training Availability Banner */}
      <div className="fixed top-34 left-80v right-4 bg-yellow-200 border border-yellow-400 rounded-lg p-3 text-center shadow-md z-10">
        <h2 className="text-sm font-semibold text-indigo-800">
          Training Available
        </h2>
        <p className="text-xs text-gray-700">Online Class | Physical Class</p>
        <button
          onClick={handleEnroll}
          className="mt-2 px-4 py-1 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 transition duration-300"
        >
          Enroll Now
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-2 pt-16 pb-16">
        {/* Course Overview */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("courseOverview")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.courseOverview.title}
            <span>{expandedSections["courseOverview"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["courseOverview"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.courseOverview.content}
            </div>
          )}
        </div>

        {/* Daily Schedule */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("dailySchedule")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.dailySchedule.title}
            <span>{expandedSections["dailySchedule"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["dailySchedule"] && (
            <div className="p-1 bg-white">
              {syllabusData.dailySchedule.days.map((day, index) => (
                <div
                  key={index}
                  className="mb-1 border border-gray-100 rounded-lg"
                >
                  <h3
                    onClick={() => toggleSection(`day${index}`)}
                    className="bg-gray-50 p-1 cursor-pointer flex justify-between items-center text-xs font-medium text-gray-600 hover:bg-gray-100"
                  >
                    {day.day}
                    <span>{expandedSections[`day${index}`] ? "▲" : "▼"}</span>
                  </h3>
                  {expandedSections[`day${index}`] && (
                    <div className="p-1 bg-white text-gray-600 leading-tight">
                      <p className="font-bold text-sm">Objective:</p>{" "}
                      {day.objective}
                      {day.topics}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Learning Outcomes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("learningOutcomes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.learningOutcomes.title}
            <span>{expandedSections["learningOutcomes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["learningOutcomes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.learningOutcomes.content}
            </div>
          )}
        </div>

        {/* Final Project Ideas */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("finalProjectIdeas")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.finalProjectIdeas.title}
            <span>{expandedSections["finalProjectIdeas"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["finalProjectIdeas"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.finalProjectIdeas.content}
            </div>
          )}
        </div>

        {/* Additional Notes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("additionalNotes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.additionalNotes.title}
            <span>{expandedSections["additionalNotes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["additionalNotes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.additionalNotes.content}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DigitalMarketing;
